/**
 * POPSTAR profile — the named-account dashboard, rebuilt to mirror the legacy
 * POPSTAR "ui-nav-dashboard" home (docs/legacy/AVOID_LEGACY_ANALYSIS.md §2.4 +
 * §2.8). The original dashboard centred the player on a circular XP arc (center
 * (792, 864), 70px radius, orange, start 270°, sweep ∝ progress) flanked by their
 * name + level, a token wallet, the prominently-shown EQUIPPED sticker, the
 * skill/seasonal/secret collection counts, and their featured starred run. This
 * reproduces that information architecture with the glamorous glass treatment.
 *
 * NAMED-ONLY: the parent ({@link import('./NetworkShell').NetworkShell}) only
 * mounts this for `named` users, but we still defend against
 * {@link RequiresAccountError} (and any load failure) by rendering an inline
 * message instead of crashing.
 *
 * Presentational + human-timescale: it loads the profile once on mount via the
 * {@link GameServices} read methods (`getProfile` / `getStickerCatalog` /
 * `getMyReplays`), derives the level/progress with the pure {@link levelForXp}
 * core helper, and holds no per-frame state. It talks to the backend ONLY through
 * the {@link GameServices} facade.
 *
 * SHOWCASE: the equipped sticker and the user's FEATURED starred replay are the
 * two things others see on the public `cards/{uid}` card. We mirror that here so
 * a player sees their own card: the featured starred run is the most-recently
 * starred id ({@link UserProfile.starredReplayIds}, last entry) resolved against
 * {@link GameServices.getMyReplays}. A "Watch" button hands the run id up via the
 * {@link ProfileScreenProps.onWatch} prop — the shell owns the replay player; this
 * screen stays presentational and never plays anything itself.
 */
import { useCallback, useEffect, useState } from 'react';
import { levelForXp } from '../core/xp';
import { rankForLevel } from '../core/ranks';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { RunSummary, StickerCatalogItem, UserProfile } from '../services/dataModel';
import { useT } from './i18n';

interface ProfileScreenProps {
  services: GameServices;
  /** Back to the network shell hub. */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own back-header. */
  embedded?: boolean;
  /**
   * Watch the user's featured starred replay. Given the `runs/{runId}` id, the
   * shell loads it (via {@link GameServices.getReplay}) and plays it back. Optional:
   * when omitted, the Showcase shows the starred run but hides the Watch button.
   */
  onWatch?: (runId: string) => void;
}

/**
 * Resolve the FEATURED starred run the same way the public card does: the
 * most-recently-starred id (last of {@link UserProfile.starredReplayIds}) matched
 * against the loaded replay summaries. Returns null when nothing is starred or the
 * id isn't among the loaded runs.
 */
function findFeaturedStarred(
  profile: UserProfile,
  replays: RunSummary[],
): RunSummary | null {
  const ids = profile.starredReplayIds;
  if (ids.length === 0) return null;
  const featuredId = ids[ids.length - 1];
  return replays.find((r) => r.id === featuredId) ?? null;
}

/** Local load lifecycle for the one-shot profile fetch. */
type LoadState = 'loading' | 'ready' | 'error';

/** Resolve a sticker id to its catalog row (for the equipped showcase art / title). */
function findSticker(
  catalog: StickerCatalogItem[],
  id: string | null,
): StickerCatalogItem | null {
  if (!id) return null;
  return catalog.find((s) => s.id === id) ?? null;
}

export function ProfileScreen({ services, onBack, onWatch, embedded = false }: ProfileScreenProps) {
  const t = useT();
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [catalog, setCatalog] = useState<StickerCatalogItem[]>([]);
  const [replays, setReplays] = useState<RunSummary[]>([]);

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    try {
      const [p, cat, runs] = await Promise.all([
        services.getProfile(),
        services.getStickerCatalog(),
        services.getMyReplays(),
      ]);
      setProfile(p);
      setCatalog(cat);
      setReplays(runs);
      setState('ready');
    } catch (err) {
      setError(
        err instanceof RequiresAccountError
          ? 'Log in to view your profile.'
          : err instanceof Error
            ? err.message
            : 'Could not load your profile.',
      );
      setState('error');
    }
  }, [services]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={'screen network-screen profile-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{PROFILE_CSS}</style>
      {!embedded && (
        <header className="network-head">
          <button className="btn btn-ghost network-back" onClick={onBack} type="button">
            ‹ Back
          </button>
          <h1 className="network-title">{t('profile.title')}</h1>
        </header>
      )}

      {state === 'loading' && <p className="muted-line">{t('profile.loading')}</p>}

      {state === 'error' && (
        <div className="panel">
          <p className="auth-error">{error}</p>
          <button className="btn btn-ghost" type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {state === 'ready' && profile && (
        <ProfileBody
          profile={profile}
          catalog={catalog}
          replays={replays}
          onWatch={onWatch}
        />
      )}
    </div>
  );
}

/** The rendered profile once data is present (kept separate so it can assume non-null). */
function ProfileBody({
  profile,
  catalog,
  replays,
  onWatch,
}: {
  profile: UserProfile;
  catalog: StickerCatalogItem[];
  replays: RunSummary[];
  onWatch?: (runId: string) => void;
}) {
  const t = useT();
  const lvl = levelForXp(profile.xp);
  const rank = rankForLevel(lvl.level);
  // Guard against a zero/garbage span so the arc/progress stays a sane fraction.
  const span = lvl.intoLevel + lvl.toNext;
  const fraction = span > 0 ? lvl.intoLevel / span : 0;
  const pct = Math.round(fraction * 100);

  const equipped = findSticker(catalog, profile.equipped.slot1);
  // The single replay surfaced on the public card, alongside the equipped sticker.
  const featured = findFeaturedStarred(profile, replays);

  return (
    <>
      {/*
        HERO — the legacy dashboard centrepiece (§2.4 XP arc + §2.8 level math):
        the player's name + level wrap the circular XP arc that fills toward the
        next level, with the token wallet riding alongside.
      */}
      <section className="panel profile-hero">
        <div className="profile-headline">
          <div className="profile-avatar" aria-hidden>
            {profile.name.charAt(0).toUpperCase() || '★'}
          </div>
          <div className="profile-name-block">
            <span className="profile-name">{profile.name}</span>
            <span className="profile-level">
              Lv {lvl.level} · <span className="profile-rank-name">{rank.name}</span>
            </span>
            <span className="profile-rank-tier">
              {rank.tierRoman} · {rank.tierName}
            </span>
          </div>
          <div className="profile-wallet" title="Token wallet">
            <span className="profile-wallet-coin" aria-hidden>
              ◈
            </span>
            <span className="profile-wallet-value">{profile.tokens.toLocaleString()}</span>
            <span className="profile-wallet-label">tokens</span>
          </div>
        </div>

        <XpArc level={lvl.level} fraction={fraction} pct={pct} />

        <p className="profile-xp-caption">
          {lvl.intoLevel.toLocaleString()} / {span.toLocaleString()} XP —{' '}
          {lvl.toNext.toLocaleString()} to Level {lvl.level + 1}
        </p>
      </section>

      {/*
        EQUIPPED — showcased prominently (§2.4 "equipped sticker image loaded
        from CDN"): the slot-1 sticker the world sees on the public card.
      */}
      <section className="panel profile-equipped">
        <h2>{t('profile.equippedSticker')}</h2>
        <EquippedShowcase sticker={equipped} />
      </section>

      {/* COLLECTION — the skill / seasonal / secret tallies (§2.4 USER_INFO counts). */}
      <section className="panel profile-counts">
        <h2>{t('profile.collection')}</h2>
        <div className="count-row">
          <CountStat label={t('profile.skill')} value={profile.skillStickerCount} />
          <CountStat label={t('profile.seasonalCount')} value={profile.seasonStickerCount} />
          <CountStat label={t('profile.secret')} value={profile.secretStickerCount} />
        </div>
      </section>

      {/* STARRED REPLAY — the featured run on the public card, with Watch (§2.8/showcase). */}
      <section className="panel profile-replay">
        <h2>{t('profile.featuredReplay')}</h2>
        <p className="profile-replay-sub">{t('profile.featuredSub')}</p>
        <StarredReplay replay={featured} onWatch={onWatch} />
      </section>
    </>
  );
}

/**
 * The circular XP progress arc — the literal centrepiece of the legacy dashboard
 * (§2.4: center, 70px radius, orange, start 270°, sweep ∝ progress). Rendered as
 * an SVG ring: a faint track plus a foreground stroke whose dash offset encodes
 * the fraction into the current level, sweeping clockwise from the top (12
 * o'clock = legacy "start 270°"). Pure presentation; no animation state.
 */
function XpArc({ level, fraction, pct }: { level: number; fraction: number; pct: number }) {
  const size = 180;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  // Clamp defensively so a bad read can't produce a negative / overlong arc.
  const clamped = Math.max(0, Math.min(1, fraction));
  const dashOffset = circumference * (1 - clamped);

  return (
    <div
      className="xp-arc"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={`Experience toward level ${level + 1}`}
    >
      <svg className="xp-arc-svg" viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <defs>
          <linearGradient id="xp-arc-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--accent)" />
            <stop offset="100%" stopColor="var(--gold)" />
          </linearGradient>
        </defs>
        {/* Track: the full ring, faint. */}
        <circle
          className="xp-arc-track"
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={stroke}
        />
        {/* Progress: rotated −90° so the dash starts at 12 o'clock and sweeps clockwise. */}
        <circle
          className="xp-arc-fill"
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>
      <div className="xp-arc-center">
        <span className="xp-arc-level">{level}</span>
        <span className="xp-arc-level-label">Level</span>
        <span className="xp-arc-pct">{pct}%</span>
      </div>
    </div>
  );
}

/** A small labelled count (skill / seasonal / secret sticker tallies). */
function CountStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="count-stat">
      <span className="count-value">{value.toLocaleString()}</span>
      <span className="count-label">{label}</span>
    </div>
  );
}

/**
 * The equipped slot-1 sticker, shown large and prominently (the legacy dashboard
 * featured the equipped sticker as a hero element). Falls back to an inviting
 * empty state when nothing is equipped.
 */
function EquippedShowcase({ sticker }: { sticker: StickerCatalogItem | null }) {
  const t = useT();
  return (
    <div className="equipped-showcase" data-filled={sticker ? 'true' : 'false'}>
      <div className="equipped-art-frame">
        {sticker ? (
          <img className="equipped-art" src={sticker.imageUrl} alt={sticker.title} />
        ) : (
          <span className="equipped-empty" aria-hidden>
            ★
          </span>
        )}
      </div>
      <div className="equipped-meta">
        {sticker ? (
          <>
            <span className="equipped-title">{sticker.title}</span>
            <span className="equipped-desc">{sticker.desc}</span>
          </>
        ) : (
          <>
            <span className="equipped-title">{t('profile.noEquipped')}</span>
            <span className="equipped-desc muted-line">
              Equip one from your collection to show it off on your card.
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** Human-readable label for which attack family ended the run (or a fallback). */
function deathLabel(family: RunSummary['deathFamily']): string {
  if (!family) return 'Survived';
  // Capitalise the family token ('walls' → 'Walls') for the caption.
  return family.charAt(0).toUpperCase() + family.slice(1);
}

/**
 * The featured starred replay as it appears on the public card: headline score,
 * how the run ended, and (when {@link ProfileScreenProps.onWatch} is provided) a
 * Watch button that hands the run id up to the shell's replay player.
 */
function StarredReplay({
  replay,
  onWatch,
}: {
  replay: RunSummary | null;
  onWatch?: (runId: string) => void;
}) {
  const t = useT();
  if (!replay) {
    return (
      <div className="replay-card replay-card-empty">
        <span className="replay-empty-icon" aria-hidden>
          ▶
        </span>
        <span className="replay-empty-text muted-line">
          {t('profile.starToFeature')}
        </span>
      </div>
    );
  }

  return (
    <div className="replay-card">
      <div className="replay-stats">
        <div className="replay-score-block">
          <span className="replay-score">{replay.score.toLocaleString()}</span>
          <span className="replay-score-label">score</span>
        </div>
        <div className="replay-detail">
          <span className="replay-death">{deathLabel(replay.deathFamily)}</span>
          <span className="replay-duration">
            {Math.round(replay.durationMs / 1000)}s survived
          </span>
        </div>
      </div>
      {onWatch && (
        <button
          className="btn btn-primary replay-watch"
          type="button"
          onClick={() => onWatch(replay.id)}
        >
          ▶ Watch
        </button>
      )}
    </div>
  );
}

/**
 * Screen-scoped styles for the rebuilt profile dashboard (nested under
 * `.profile-screen`). Shared primitives (`.panel`, `.btn`, `.muted-line`, the XP
 * gradient, and the POPSTAR palette re-map) come from src/index.css; only the
 * dashboard-specific hero / arc / equipped / replay layout lives here so the
 * legacy information architecture can be reproduced without touching the system.
 */
const PROFILE_CSS = `
/* HERO — name + level + wallet over the centred XP arc. */
.profile-screen .profile-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  text-align: center;
}
.profile-screen .profile-headline {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
}
.profile-screen .profile-name-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  text-align: left;
}
.profile-screen .profile-wallet {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 0 0 auto;
  background: var(--panel-2);
  border: 1px solid rgba(255, 209, 102, 0.3);
  border-radius: var(--radius);
  padding: 8px 14px;
}
.profile-screen .profile-wallet-coin {
  color: var(--gold);
  font-size: 14px;
  line-height: 1;
}
.profile-screen .profile-wallet-value {
  font-size: 22px;
  font-weight: 800;
  color: var(--gold);
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.profile-screen .profile-wallet-label {
  font-size: 10px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* XP ARC — the legacy dashboard centrepiece (§2.4). */
.profile-screen .xp-arc {
  position: relative;
  width: 180px;
  height: 180px;
}
.profile-screen .xp-arc-svg {
  width: 100%;
  height: 100%;
  display: block;
  filter: drop-shadow(0 6px 18px var(--accent-glow));
}
.profile-screen .xp-arc-track {
  stroke: var(--panel-2);
}
.profile-screen .xp-arc-fill {
  stroke: url(#xp-arc-grad);
  transition: stroke-dashoffset var(--t-slow) var(--ease);
}
.profile-screen .xp-arc-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
}
.profile-screen .xp-arc-level {
  font-size: 44px;
  font-weight: 900;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(96deg, var(--accent-soft), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.profile-screen .xp-arc-level-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--muted);
  margin-top: 2px;
}
.profile-screen .xp-arc-pct {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-dim);
  font-variant-numeric: tabular-nums;
  margin-top: 6px;
}
.profile-screen .profile-xp-caption {
  margin: 0;
  font-size: 13px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}

/* EQUIPPED — the prominently-shown slot-1 sticker. */
.profile-screen .equipped-showcase {
  display: flex;
  align-items: center;
  gap: 16px;
}
.profile-screen .equipped-art-frame {
  flex: 0 0 auto;
  width: 84px;
  height: 84px;
  border-radius: var(--radius);
  display: grid;
  place-items: center;
  background: var(--panel-2);
  border: 1px dashed var(--glass-border-strong);
  overflow: hidden;
}
.profile-screen .equipped-showcase[data-filled='true'] .equipped-art-frame {
  border-style: solid;
  border-color: var(--gold);
  box-shadow: 0 0 22px rgba(255, 209, 102, 0.3);
}
.profile-screen .equipped-art {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.profile-screen .equipped-empty {
  font-size: 38px;
  color: var(--muted);
  opacity: 0.5;
}
.profile-screen .equipped-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.profile-screen .equipped-title {
  font-size: 17px;
  font-weight: 800;
  color: var(--text);
}
.profile-screen .equipped-desc {
  font-size: 13px;
  color: var(--muted);
  line-height: 1.4;
}

/* FEATURED REPLAY. */
.profile-screen .profile-replay-sub {
  margin: -4px 0 14px;
  color: var(--muted);
  font-size: 13px;
}
.profile-screen .replay-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
  padding: 16px;
}
.profile-screen .replay-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}
.profile-screen .replay-score-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.profile-screen .replay-score {
  font-size: 30px;
  font-weight: 900;
  line-height: 1;
  color: var(--accent-soft);
  font-variant-numeric: tabular-nums;
}
.profile-screen .replay-score-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
}
.profile-screen .replay-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.profile-screen .replay-death {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}
.profile-screen .replay-duration {
  font-size: 12px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.profile-screen .replay-watch {
  flex: 0 0 auto;
  padding: 10px 20px;
  font-size: 15px;
}
.profile-screen .replay-card-empty {
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  border-style: dashed;
}
.profile-screen .replay-empty-icon {
  font-size: 28px;
  color: var(--muted);
  opacity: 0.5;
}
.profile-screen .replay-empty-text {
  font-size: 13px;
}

@media (max-width: 380px) {
  .profile-screen .replay-card {
    flex-direction: column;
    align-items: stretch;
  }
  .profile-screen .replay-watch {
    width: 100%;
  }
}
`;
